import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Users as UsersIcon, MapPin, Radio } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useLiveEvents } from "@/lib/liveEvents";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

export default function Groups() {
  const { user } = useAuth();
  const { on } = useLiveEvents();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", radius_km: 5 });
  const [hangoutsByGroup, setHangoutsByGroup] = useState({}); // group_id -> hangout

  const load = async () => {
    const { data } = await api.get("/groups");
    setGroups(data.groups);
    // Fetch active hangouts for joined groups (parallel)
    const joined = data.groups.filter((g) => g.members?.includes(user?.id));
    const results = await Promise.all(
      joined.map((g) => api.get(`/groups/${g.id}/hangout`).then((r) => [g.id, r.data.hangout]).catch(() => [g.id, null]))
    );
    const map = {};
    for (const [gid, h] of results) if (h) map[gid] = h;
    setHangoutsByGroup(map);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh when any hangout changes so join/start pulses show up live.
  useEffect(() => {
    const unsub = on("hangout_change", () => load());
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/groups", {
        ...form,
        lat: user?.lat ?? 40.71,
        lng: user?.lng ?? -74.0,
      });
      toast.success("Broadcast channel created");
      setOpen(false);
      setForm({ name: "", description: "", radius_km: 5 });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const join = async (id) => {
    await api.post(`/groups/${id}/join`);
    toast.success("Joined broadcast");
    load();
  };

  const startHangout = async (groupId) => {
    try {
      await api.post(`/groups/${groupId}/hangouts/start`, { duration_minutes: 120 });
      toast.success("Hangout started — head to the radar", {
        description: "Group members can now see each other's live location.",
      });
      navigate("/radar");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start hangout");
    }
  };

  const joinHangout = async (hangoutId) => {
    try {
      await api.post(`/hangouts/${hangoutId}/join`);
      toast.success("Joined hangout");
      navigate("/radar");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not join hangout");
    }
  };

  return (
    <div className="px-4 md:px-8 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs tracking-[0.3em] font-mono text-[#00E5FF]">// BROADCAST GROUPS</div>
          <h1 className="font-[Unbounded] text-3xl md:text-4xl font-bold tracking-tighter mt-2">Channels near you</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="create-group-btn" className="inline-flex items-center gap-2 bg-[#00E5FF] text-black px-4 py-2 rounded-full font-semibold hover:glow-primary transition-all">
              <Plus size={16} /> New
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0D0E15] border border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="font-[Unbounded]">Create broadcast group</DialogTitle>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4 mt-2">
              <input
                data-testid="group-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Channel name"
                className="w-full bg-[#05050A] border border-white/10 rounded-lg px-4 py-3 focus:border-[#00E5FF] focus:outline-none"
              />
              <textarea
                data-testid="group-desc-input"
                required
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What's this signal for?"
                rows={3}
                className="w-full bg-[#05050A] border border-white/10 rounded-lg px-4 py-3 focus:border-[#00E5FF] focus:outline-none"
              />
              <div>
                <label className="text-[10px] tracking-widest font-mono text-white/50">RADIUS · {form.radius_km}KM</label>
                <input
                  type="range"
                  min={1}
                  max={50}
                  value={form.radius_km}
                  onChange={(e) => setForm({ ...form, radius_km: parseFloat(e.target.value) })}
                  className="w-full mt-2 accent-[#00E5FF]"
                  data-testid="group-radius-input"
                />
              </div>
              <button data-testid="submit-group-btn" className="w-full bg-[#00E5FF] text-black font-semibold py-3 rounded-full hover:glow-primary transition-all">Broadcast</button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {groups.length === 0 ? (
        <div className="p-8 bg-[#0D0E15] border border-white/10 rounded-2xl text-center text-white/50">
          No broadcast channels yet. Be the first to open one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map((g) => {
            const joined = g.members?.includes(user?.id);
            const hangout = hangoutsByGroup[g.id];
            const iAmInHangout = hangout?.active_member_ids?.includes(user?.id);
            return (
              <div key={g.id} data-testid={`group-card-${g.id}`} className="p-6 bg-[#0D0E15] border border-white/10 rounded-2xl hover:border-[#00E5FF] transition-all">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-[Unbounded] text-lg">{g.name}</h3>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-white/10 text-white/60">
                    <MapPin size={10} className="inline mr-1" />{g.distance_km ?? "-"}km · r{g.radius_km}
                  </span>
                </div>
                <p className="text-sm text-white/60 line-clamp-3 mb-4">{g.description}</p>
                {joined && hangout && (
                  <div data-testid={`hangout-pulse-${g.id}`} className="mb-3 p-2 rounded-xl border border-[#FF007F]/40 bg-[#FF007F]/[0.06] flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="relative flex h-2 w-2 shrink-0">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-[#FF007F] opacity-75 animate-ping" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF007F]" />
                      </span>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[#FF007F] truncate">
                        LIVE HANGOUT · {(hangout.active_member_ids || []).length}
                      </div>
                    </div>
                    {!iAmInHangout && (
                      <button
                        data-testid={`join-hangout-${g.id}`}
                        onClick={() => joinHangout(hangout.id)}
                        className="text-[10px] font-semibold px-3 py-1 rounded-full bg-[#FF007F] text-white hover:brightness-110 transition-all"
                      >
                        Join
                      </button>
                    )}
                    {iAmInHangout && (
                      <span data-testid={`in-hangout-${g.id}`} className="text-[10px] font-mono uppercase text-[#39FF14]">IN</span>
                    )}
                  </div>
                )}
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-mono text-white/40 inline-flex items-center gap-1"><UsersIcon size={10} /> {g.members?.length ?? 0}</span>
                  <div className="flex items-center gap-2">
                    {joined && !hangout && (
                      <button
                        data-testid={`start-hangout-${g.id}`}
                        onClick={() => startHangout(g.id)}
                        title="Start a live hangout — group members can see each other on the radar"
                        className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#FF007F]/50 text-[#FF007F] hover:bg-[#FF007F] hover:text-white transition-all inline-flex items-center gap-1"
                      >
                        <Radio size={11} /> Start hangout
                      </button>
                    )}
                    <button
                      data-testid={`join-group-${g.id}`}
                      disabled={joined}
                      onClick={() => join(g.id)}
                      className={`text-xs font-semibold px-4 py-1.5 rounded-full transition-all ${
                        joined ? "bg-white/5 text-white/40" : "bg-[#39FF14] text-black hover:glow-secondary"
                      }`}
                    >
                      {joined ? "Joined" : "Join"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
